<template>
    <div class="test-landing">
        <div class="test-landing__badge">⚠ TEST ENVIRONMENT</div>

        <div class="test-landing__card">
            <div class="test-landing__logo">🎮 GameArtKit</div>
            <h1 class="test-landing__headline">The #1 platform for game artists</h1>

            <div class="test-landing__ref">
                <span class="test-landing__ref-label">Partner ref:</span>
                <span class="test-landing__ref-value" :class="{ 'test-landing__ref-value--empty': !ref }">
                    {{ ref || 'not provided' }}
                </span>
            </div>

            <a
                v-if="ref"
                :href="ctaHref"
                class="test-landing__cta"
                @click="onCtaClick"
            >
                → Go to GameArtKit Portal
            </a>

            <div class="test-landing__divider">── Manual test ──────────────────────────</div>

            <div class="test-landing__manual">
                <input
                    v-model="manualSlug"
                    class="test-landing__input"
                    type="text"
                    placeholder="Enter slug..."
                />
                <button class="test-landing__open-btn" @click="openManual">Open Link</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TestLandingPage',

    data() {
        return {
            ref: null,
            manualSlug: '',
        };
    },

    computed: {
        portalUrl() {
            return process.env.VUE_APP_PORTAL_URL || 'http://localhost:8080';
        },
        ctaHref() {
            return `${this.portalUrl}/?ref=${this.ref}`;
        },
    },

    mounted() {
        const ref = this.$route.query.ref;
        if (ref) {
            this.ref = ref;
            // Persist the slug in a cookie readable by the frontend on :8080.
            // Cookies on localhost are not port-scoped, so this is the reliable
            // mechanism to carry the attribution slug across origins on localhost.
            const maxAge = 60 * 60 * 24 * 30; // 30 days
            document.cookie = `gak_ref=${encodeURIComponent(ref)}; max-age=${maxAge}; path=/; SameSite=Lax`;
            console.debug('[TestLandingPage] stored gak_ref cookie for slug:', ref);

            // Fire tracking pixel — mirrors what the Tilda landing page <img> tag does.
            // This records the visit in the backend so traffic is counted correctly.
            const backendUrl = process.env.VUE_APP_BACKEND_URL || 'http://127.0.0.1:8000';
            const px = new Image();
            px.src = `${backendUrl}/t/${encodeURIComponent(ref)}?pixel=1`;
            console.debug('[TestLandingPage] fired pixel for slug:', ref);
        } else {
            console.debug('[TestLandingPage] no ref in URL — showing manual form');
        }
    },

    methods: {
        onCtaClick() {
            console.debug('[TestLandingPage] navigating to portal:', this.ctaHref);
        },
        openManual() {
            if (!this.manualSlug.trim()) return;
            const href = `${this.portalUrl}/?ref=${this.manualSlug.trim()}`;
            console.debug('[TestLandingPage] navigating to portal (manual):', href);
            window.location.href = href;
        },
    },
};
</script>

<style scoped>
.test-landing {
    min-height: 100vh;
    background: #0f172a;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: system-ui, -apple-system, sans-serif;
    color: #f1f5f9;
    padding: 24px;
}

.test-landing__badge {
    position: fixed;
    top: 12px;
    right: 12px;
    background: #f59e0b;
    color: #1c1917;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 4px;
    letter-spacing: 0.05em;
}

.test-landing__card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 12px;
    padding: 48px 40px;
    max-width: 560px;
    width: 100%;
}

.test-landing__logo {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 8px;
    color: #10b981;
}

.test-landing__headline {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 28px;
    color: #f1f5f9;
}

.test-landing__ref {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
    font-size: 14px;
}

.test-landing__ref-label {
    color: #94a3b8;
}

.test-landing__ref-value {
    font-family: monospace;
    background: #0f172a;
    padding: 2px 8px;
    border-radius: 4px;
    color: #10b981;
}

.test-landing__ref-value--empty {
    color: #64748b;
    font-style: italic;
}

.test-landing__cta {
    display: inline-block;
    background: #10b981;
    color: #fff;
    font-weight: 600;
    font-size: 15px;
    padding: 12px 24px;
    border-radius: 8px;
    text-decoration: none;
    margin-bottom: 32px;
    transition: background 0.15s;
}

.test-landing__cta:hover {
    background: #059669;
}

.test-landing__divider {
    color: #475569;
    font-size: 12px;
    margin-bottom: 16px;
}

.test-landing__manual {
    display: flex;
    gap: 8px;
}

.test-landing__input {
    flex: 1;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    color: #f1f5f9;
    padding: 8px 12px;
    font-size: 14px;
    outline: none;
}

.test-landing__input:focus {
    border-color: #10b981;
}

.test-landing__open-btn {
    background: #1d4ed8;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
}

.test-landing__open-btn:hover {
    background: #1e40af;
}
</style>
